import { Phone, Headset, ClipboardList, Code2 } from "lucide-react";

export const NAV = [
  { label: "Home", to: "/" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Professionals", to: "/professionals" },
  { label: "IT Projects", to: "/it-projects" },
  { label: "Why BleuSharks", to: "/why-bleusharks" },
  { label: "Resources", to: "/resources" },
];

export const TAGLINE = "Build the team. Cut the cost. Keep the control.";

// ---------- HERO ----------
export const HERO = {
  eyebrow: "Talent + Execution partner",
  lines: ["Build the team.", "Cut the cost.", "Keep the control."],
  sub: "Our teams are based in Pune, India, giving you access to skilled professionals at a very different employment cost from markets such as the UK. You choose who joins your team and manage their work. BleuSharks handles the hiring, payroll, HR and operational side behind them.",
  subIt: "Need an IT project delivered instead? Our technical team can handle selected projects too, with Data Migration as a core capability.",
  primary: { label: "Tell Us What You Need", to: "/contact" },
  secondary: { label: "Meet the Professionals", to: "/professionals" },
};

export const STRIP = ["Sales", "Support", "Operations", "IT"];
export const STRIP_LINE = "One partner when the work needs doing.";

// ---------- TWO WAYS ----------
export const TWO_WAYS = {
  headline: "Two ways we can help.",
  build: {
    tag: "Build your team",
    hook: "Need another capable person on your team?",
    body: "We source and screen professionals from India across sales development, customer support, back office, operations and Information Technology. You interview the shortlist and decide who joins your team.",
    cta: { label: "Meet the Professionals", to: "/professionals" },
  },
  project: {
    tag: "Hand us an IT project",
    hook: "Need the work delivered instead?",
    body: "For selected Information Technology projects, our technical team takes responsibility for the execution. Data migration is one of our core project capabilities.",
    cta: { label: "Explore IT Projects", to: "/it-projects" },
  },
};

// ---------- WHY (home short flow) ----------
export const WHY_FLOW = {
  headline: "Hiring should not become another job.",
  steps: [
    "You tell us what you need.",
    "We search and screen the people.",
    "You meet the shortlist.",
    "You make the final decision.",
    "They work with your team.",
    "We handle the employment side.",
  ],
};

export const EMPLOYMENT = {
  headline: "They work with you. They are employed by us.",
  body: "The professional works with your team every day and reports directly to you. You decide what they work on, their targets, priorities and how they fit into your team. BleuSharks remains the legal employer and keeps the professional on our payroll. We handle the employment paperwork, payroll, HR and related administration behind the scenes.",
  manage: "You manage the work. We manage the employment.",
  scale: "Scale the team up or down as your business needs change.",
  columns: [
    { title: "Your company", body: "Sets the work, targets, priorities and day-to-day direction." },
    { title: "The professional", body: "Works as part of your team and reports directly to you." },
    { title: "BleuSharks", body: "Remains the legal employer and handles payroll, HR, employment administration and ongoing support." },
  ],
  pay: "You pay BleuSharks for the professional. We take care of the salary and employment responsibilities behind that person.",
};

export const WHY_COST = {
  headline: "Why does it cost less?",
  lead: "Because your team is built in Pune, India.",
  body: [
    "Pune is one of India's major technology and business hubs, with access to experienced professionals across sales, customer support, operations and Information Technology.",
    "The difference is not about finding less capable people. It comes from the difference in employment costs between markets.",
    "You get access to skilled professionals in India without carrying the same cost structure as building every role locally.",
  ],
};

export const PRICING = {
  headline: "See what the difference looks like.",
  sub: "Here is an example for companies hiring in the UK.",
  roles: [
    { role: "Sales Development Representative", uk: "£4,000 / month", bs: "£1,800 / month" },
    { role: "Customer Support Representative", uk: "£3,500 / month", bs: "£1,500 / month" },
  ],
  notes: [
    "BleuSharks rates shown above are for full-time dedicated professionals with 2+ years of relevant experience.",
    "Pricing can vary depending on the role, experience, working hours, region and specific requirements.",
    "Hiring outside the UK? Talk to us for the applicable regional pricing.",
  ],
  cta: { label: "Tell Us What You Need", to: "/contact" },
};

export const ACCOUNT_MANAGER = {
  headline: "Someone stays close to the team.",
  body: "Every dedicated team includes operational oversight from a BleuSharks Account Manager at no additional cost. Your Account Manager stays close to the team, monitors attendance and day-to-day activity, and keeps you updated on what is happening.",
  highlight: "Daily status updates are included.",
  note: "No separate Account Manager fee.",
};

export const OFFICE = {
  headline: "Your team works from our Pune office.",
  body: "Remote work can be useful, but for our dedicated delivery teams we prefer a controlled office setup. Your professionals work from our physical office in Pune, giving the team a consistent working environment, reliable attendance and closer operational support.",
  highlight: "Same people. Same place. Every working day.",
};

export const ACCESS = {
  headline: "Your access stays on a need-to-work basis.",
  body: "Your professional only needs access to the systems and information required for their role. Client data, tools and processes remain under your direction, and access should be managed according to your internal permissions and security requirements.",
};

// ---------- CATEGORIES (4 only) ----------
export const CATEGORIES = [
  {
    key: "sales",
    title: "Sales Development",
    icon: Phone,
    color: "coral",
    blurb: "People who start conversations and keep the pipeline moving.",
    roles: ["Sales Development Representative", "Outbound Sales Representative", "Appointment Setter"],
  },
  {
    key: "support",
    title: "Customer Support",
    icon: Headset,
    color: "sky",
    blurb: "People who look after your customers with patience and care.",
    roles: ["Customer Support Representative", "Chat Support", "Email Support", "Voice Support"],
  },
  {
    key: "ops",
    title: "Back Office and Operations",
    icon: ClipboardList,
    color: "yellow",
    blurb: "People who keep the day-to-day work organised and on track.",
    roles: ["Back Office Associate", "Operations Associate", "Administrative Support", "Data Support"],
  },
  {
    key: "it",
    title: "Information Technology",
    icon: Code2,
    color: "seagreen",
    blurb: "People who build, maintain and support your technology.",
    roles: ["Software Developer", "Full Stack Developer", "Backend Developer", "Data Specialist", "Artificial Intelligence and Automation Specialist"],
  },
];

// ---------- DATA MIGRATION TEASER (home) ----------
export const MIGRATION_TEASER = {
  headline: "Moving data? We can handle that too.",
  body: "Whether your business is moving from one system to another, or your customers regularly need help moving onto your platform, our migration team can handle the work from assessment through migration and validation.",
  journey: ["Old system", "Assess and prepare", "Migrate", "Validate", "New system"],
  cta: { label: "Explore Data Migration", to: "/it-projects" },
};

// ---------- HOW IT WORKS ----------
export const HOW = {
  hero: "From requirement to a working team.",
  heroSub: "Tell us who you need. We handle the search, screening and setup. You make the final hiring decision.",
  steps: [
    { n: "01", title: "Tell us what you need", body: "We start by understanding the role, the skills and experience it needs, the working hours, how the person will communicate with your team, the tools they will use and what they will be responsible for." },
    { n: "02", title: "We search our screened talent pool", body: "Instead of starting from zero every time, we first look through professionals we have already assessed and shortlist people who match your requirement." },
    { n: "03", title: "We screen for the actual role", body: "A resume on its own is not enough. Depending on the role, screening can include communication, relevant experience, role-specific skills, practical understanding and an interview assessment." },
    { n: "04", title: "You see more than a resume", body: "You should know a candidate before you spend time interviewing them. Our candidate presentation can include a recorded video interview, screening notes, relevant experience and skills, so you understand the person behind the resume." },
    { n: "05", title: "You take the final interview", body: "We shortlist. You decide. You take the final interview and the final hiring decision always belongs to you." },
    { n: "06", title: "We handle onboarding", body: "Once you select someone, BleuSharks handles the employment setup and onboarding administration. The professional then joins your workflow." },
    { n: "07", title: "They work with you", body: "They become part of your working team. You manage the daily work, priorities, targets, tools and communication. We manage the employment and operational side." },
    { n: "08", title: "We stay involved", body: "We do not disappear once the professional starts. BleuSharks stays in the background as an operational layer, supporting both your team and the people working with you." },
  ],
  speed: {
    headline: "Your team can be ready quickly.",
    body: "For many standard requirements, our aim is to have the right professional or team ready within around one week.",
    why: "We are not always starting with an empty search. We already keep a pool of professionals we have screened, so we can begin by matching your requirement against people we have already assessed.",
  },
};

// ---------- PROFESSIONALS ----------
export const PRO = {
  hero: "People who can actually do the work.",
  heroSub: "Tell us the kind of work sitting with your team. We will help you find the right professional.",
};

// ---------- IT PROJECTS ----------
export const IT = {
  hero: "Need the project done?",
  heroSub: "For selected Information Technology work, BleuSharks can take responsibility for the execution instead of simply providing another person for your team.",
  flagshipTag: "Our flagship capability: Data Migration",
  migration: {
    headline: "Your data needs to move. Let's make sure everything that matters gets there.",
    body: "BleuSharks can help any business that needs data migrated. That includes moving from one platform to another, replacing a legacy system, bringing several systems together, moving business data into a new environment, or cleaning and preparing data before a large migration.",
    paths: [
      {
        tag: "Migration for your business",
        hook: "Need your own data moved?",
        body: "If your company is changing systems, bringing data together or moving into a new environment, our migration team can help manage the journey from assessment through migration and validation.",
      },
      {
        tag: "Migration for your customers",
        hook: "Do your customers regularly need migration help?",
        body: "This is for cloud, Customer Relationship Management, Enterprise Resource Planning and other software providers. Your new customers often have data sitting in another platform. Before they can fully use your product, that data has to move, and that becomes migration work for your team. BleuSharks can be the migration team behind you.",
      },
    ],
    partnershipHeadline: "You build the platform. We help your customers get there.",
    partnershipBody: "Every new customer should not create another migration workload for your internal team. BleuSharks can work as an extended migration arm for your business, moving customer data into your platform while working within your standards and delivery workflow.",
    whiteLabel: [
      "We can work behind your brand.",
      "The customer continues to experience your company. Our migration team works behind the scenes as part of your delivery operation.",
    ],
    philosophy: "Migration is not just a technical step. It is your customer's first experience of your platform. It should happen so smoothly that they only remember arriving.",
  },
  // Delivery journey (from Beyond the Platform white paper)
  journey: {
    think: [
      { title: "Assess", body: "We look closely at the data before anything moves." },
      { title: "Classify", body: "We sort what matters, what does not and what needs special care." },
      { title: "Intelligent purge", body: "We remove what should not travel, carefully and on purpose." },
      { title: "Archive strategy", body: "We decide what to keep safe rather than migrate." },
    ],
    execute: [
      { title: "Validate", body: "We check the data is correct and ready to move." },
      { title: "Secure migration", body: "We move the data safely into the new environment." },
      { title: "Go live", body: "The new system is ready and the data is in place." },
      { title: "Post migration support", body: "We stay on to make sure everything settles cleanly." },
    ],
  },
  partnership: {
    left: { title: "Your platform team", items: ["Product", "Customer success", "Implementation", "Platform development", "Customer relationship"] },
    centre: "One customer experience",
    right: { title: "BleuSharks migration team", items: ["Assessment", "Data preparation", "Migration", "Validation", "Migration support"] },
    line: "You keep the customer relationship. We handle the migration work assigned to us.",
    start: {
      headline: "Start with one migration.",
      body: "You do not need to make a large commitment to find out whether this works. Start with a single migration and see the quality, communication and delivery for yourself before deciding on anything bigger.",
    },
  },
  other: {
    headline: "Need something else built?",
    body: "Data migration is our flagship, but our technical team can take on other selected projects too.",
    items: ["Application development", "Web and mobile development", "Backend development", "Software development", "Integrations", "Artificial Intelligence and automation", "Customer Relationship Management automation", "Technical implementation"],
  },
};

// ---------- WHY BLEUSHARKS ----------
export const WHY = {
  hero: "Good people exist. Good opportunities exist. Getting the two together should not be this complicated.",
  intro: "Companies need capable people. But hiring brings a lot with it.",
  problems: ["Search time", "Screening", "Interviews", "Payroll", "Employment administration", "Operational setup", "Replacement risk", "Scaling difficulty"],
  bridge: "At the same time, capable professionals in India can work successfully with companies around the world. BleuSharks exists to make that connection easier.",
  second: "And sometimes you do not need another employee. You just need the work done.",
  twoWays: "That is why BleuSharks works in two ways: build your team, or hand us the IT project.",
  reasons: [
    { n: "01", title: "Lower employment cost", body: "Build your team in Pune, India and access experienced professionals without carrying the same employment cost as building every role locally." },
    { n: "02", title: "Less employment work", body: "We source, screen, employ, handle payroll, HR and the operational side behind the team." },
    { n: "03", title: "You keep control", body: "You interview the shortlist, make the final selection and manage the professional's day-to-day work." },
  ],
  itPath: "Need the project delivered instead? For selected Information Technology work, BleuSharks can take responsibility for the execution, with Data Migration as a core capability.",
  transparency: {
    headline: "No mystery behind the work.",
    body: "We keep things clear and easy to follow, so working with us never feels like a black box.",
    points: [
      { title: "Know who", body: "You know who is working with you." },
      { title: "Know what", body: "You know what they are responsible for." },
      { title: "Know where", body: "You know where the work stands." },
    ],
  },
  story: {
    headline: "Our story",
    body: "We kept seeing the same thing. Businesses wanted good people. Talented professionals wanted good opportunities. But the two stayed far apart, buried under layers, admin and uncertainty. So we built something simpler: a way for companies to get capable people, keep communication clear, and when the job calls for it, have someone take responsibility for getting it done.",
  },
};

// ---------- RESOURCES ----------
export const RESOURCES = {
  hero: "Ideas worth reading.",
  heroSub: "Short, honest papers on how we think about building teams and moving data. The website gives you the clarity. These go a little deeper.",
  building: {
    title: "Building a team?",
    cards: [
      { name: "Beyond Hiring: US Edition", desc: "How we help companies in the United States build dedicated teams from India, and how we handle the recruiting, payroll, human resources and operations so you can focus on the work.", cta: "Read the US White Paper", key: "us" },
      { name: "Beyond Hiring: UK Edition", desc: "The same thinking for companies in the United Kingdom. We recruit, you approve. Every person is shortlisted by us, interviewed by us and selected by you.", cta: "Read the UK White Paper", key: "uk" },
    ],
  },
  migration: {
    title: "Moving data or running a platform?",
    card: { name: "Beyond the Platform", subtitle: "Migration is not a technical step. It is your first customer experience.", desc: "For cloud, Customer Relationship Management, Enterprise Resource Planning and software providers whose customers need their data moved. How BleuSharks can work quietly behind your brand as your migration team.", cta: "Read Beyond the Platform", key: "platform" },
  },
};

// ---------- FAQ ----------
export const FAQ = [
  { q: "Do you provide individual professionals?", a: "Yes. You can start with a single professional who works as part of your team, or build a larger team over time." },
  { q: "Can your people work with our existing team?", a: "That is the whole idea. The professional works as part of your team, reports directly to you and follows your systems and workflows." },
  { q: "Who makes the final hiring decision?", a: "You do. We shortlist and present candidates, you take the final interview, and you decide who joins your team." },
  { q: "Who employs the professional?", a: "BleuSharks handles the employment administration, payroll, human resources and operational support. You manage the work. You pay BleuSharks." },
  { q: "Can we scale the team up or down?", a: "Yes. You can add people as your requirements change, without carrying the full burden of building every role internally." },
  { q: "Do you only provide developers for Information Technology?", a: "No. For Information Technology we can either provide professionals for your team, or take responsibility for delivering a project, such as a data migration." },
  { q: "What is your flagship project capability?", a: "Data migration. We can move data for your own business, or work behind your brand to move data for your customers." },
  { q: "Where are your professionals based?", a: "Our professionals are based in India." },
  { q: "How do we get started?", a: "Tell us what you need. We search and screen, you meet the shortlist, and work begins." },
];

// ---------- CANDIDATES ----------
export const CANDIDATE = {
  headline: "Want to work with BleuSharks?",
  body: "We are always interested in meeting capable professionals across sales, customer support, operations and technology.",
  cta: "Work With Us",
};

// ---------- FINAL CTA ----------
export const FINAL_CTA = {
  headline: "Got work that needs doing?",
  sub: "Tell us what is sitting on your team's plate. We'll figure out where BleuSharks can help.",
  primary: { label: "Tell Us What You Need", to: "/contact" },
  secondary: { label: "Explore IT Projects", to: "/it-projects" },
};
